import React, { useState, useEffect } from "react";
import { api } from "../utils/api";

// ── Helpers ──────────────────────────────────────────────────
function fmtDate(d) {
  if (!d) return "—";
  return new Date(d).toLocaleDateString("fr-FR", { day: "2-digit", month: "short", year: "2-digit" });
}

function timeAgo(d) {
  if (!d) return "—";
  const s = Math.floor((Date.now() - new Date(d)) / 1000);
  if (s < 60) return "à l'instant";
  if (s < 3600) return `il y a ${Math.floor(s / 60)}min`;
  if (s < 86400) return `il y a ${Math.floor(s / 3600)}h`;
  return `il y a ${Math.floor(s / 86400)}j`;
}

// ── Micro composants ─────────────────────────────────────────
function KPI({ icon, label, value, sub, accent, alert, success }) {
  return (
    <div className={`flex flex-col gap-1 rounded-2xl border p-4 transition-all ${
      alert   ? "border-orange-500/30 bg-orange-500/5" :
      success ? "border-green-500/30 bg-green-500/5"   :
      accent  ? "border-sensoria-accent/25 bg-sensoria-accent/5" :
                "border-sensoria-border/30 bg-sensoria-card"
    }`}>
      <div className="flex items-center justify-between">
        <span className="text-xl leading-none">{icon}</span>
        {alert && <span className="rounded-full bg-orange-500/20 px-2 py-0.5 text-[10px] font-bold text-orange-400">⚠</span>}
      </div>
      <div className={`text-2xl font-bold tabular-nums ${
        success ? "text-green-300" : accent ? "text-sensoria-accent-light" : alert ? "text-orange-300" : "text-white"
      }`}>
        {value ?? "…"}
      </div>
      <div className="text-xs font-medium text-sensoria-text-dim">{label}</div>
      {sub && <div className="text-[10px] text-sensoria-muted">{sub}</div>}
    </div>
  );
}

function SectionTitle({ children }) {
  return (
    <h2 className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-widest text-sensoria-muted">
      <span className="h-px flex-1 bg-sensoria-border/40" />
      {children}
      <span className="h-px flex-1 bg-sensoria-border/40" />
    </h2>
  );
}

function PlanBadge({ plan }) {
  const map = {
    vip:     "bg-amber-500/15 text-amber-300",
    premium: "bg-sensoria-accent/15 text-sensoria-accent-light",
    pro:     "bg-emerald-500/15 text-emerald-300",
    free:    "bg-white/5 text-sensoria-muted",
  };
  return (
    <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${map[plan] || map.free}`}>
      {plan || "free"}
    </span>
  );
}

function VerifiedBadge() {
  return (
    <span
      title="Profil certifié"
      className="inline-flex items-center justify-center h-4 w-4 rounded-full bg-sensoria-accent text-white text-[9px] font-bold shrink-0"
    >
      ✓
    </span>
  );
}

// Sparkline SVG inline
function Sparkline({ data, width = 160, height = 40 }) {
  if (!data || data.length < 2) return <span className="text-xs text-sensoria-muted">—</span>;
  const max = Math.max(...data.map(d => d.count), 1);
  const pts = data.map((d, i) => {
    const x = (i / (data.length - 1)) * width;
    const y = height - (d.count / max) * (height - 4) - 2;
    return `${x},${y}`;
  }).join(" ");
  return (
    <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`} className="overflow-visible">
      <polyline points={pts} fill="none" stroke="#8b6ff5" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      <polyline
        points={`0,${height} ${pts} ${width},${height}`}
        fill="url(#spark-fill)" stroke="none"
      />
      <defs>
        <linearGradient id="spark-fill" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#8b6ff5" stopOpacity="0.25" />
          <stop offset="100%" stopColor="#8b6ff5" stopOpacity="0" />
        </linearGradient>
      </defs>
    </svg>
  );
}

// ── Toast mini ────────────────────────────────────────────────
function Toast({ msg, onDone }) {
  useEffect(() => {
    if (!msg) return;
    const t = setTimeout(onDone, 3000);
    return () => clearTimeout(t);
  }, [msg, onDone]);
  if (!msg) return null;
  return (
    <div className="fixed bottom-4 right-4 z-50 rounded-xl border border-sensoria-accent/30 bg-sensoria-card px-4 py-3 text-sm text-white shadow-xl">
      {msg}
    </div>
  );
}

// ── Dashboard ─────────────────────────────────────────────────
const TABS = [
  { key: "overview",      label: "Vue d'ensemble", icon: "📊" },
  { key: "users",         label: "Inscrits",        icon: "👥" },
  { key: "activation",    label: "Activation",      icon: "🚀" },
  { key: "verification",  label: "Vérification",    icon: "✓"  },
];

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [tab, setTab] = useState("overview");
  const [search, setSearch] = useState("");

  // états actions
  const [notified,    setNotified]    = useState(new Set()); // relance DB (legacy)
  const [reminded,    setReminded]    = useState(new Set()); // relance email envoyée
  const [verified,    setVerified]    = useState(new Set()); // certifiés cette session
  const [unverified,  setUnverified]  = useState(new Set()); // décertifiés cette session
  const [rejected,    setRejected]    = useState(new Set()); // demandes refusées cette session
  const [loadingIds,  setLoadingIds]  = useState(new Set()); // spinner par userId
  const [toast,       setToast]       = useState("");

  // test email
  const [testType,    setTestType]    = useState("reminder");
  const [testLoading, setTestLoading] = useState(false);
  const [testResult,  setTestResult]  = useState(null); // { ok, msg }

  useEffect(() => {
    api("/api/admin/stats")
      .then(setStats)
      .catch(err => setError(err.message || "Accès refusé."))
      .finally(() => setLoading(false));
  }, []);

  function addLoading(id)    { setLoadingIds(s => new Set([...s, id])); }
  function removeLoading(id) { setLoadingIds(s => { const n = new Set(s); n.delete(id); return n; }); }

  async function sendReminder(userId) {
    addLoading(userId);
    try {
      const r = await api(`/api/admin/send-reminder/${userId}`, { method: "POST" });
      setReminded(prev => new Set([...prev, userId]));
      setToast(`✉ Email envoyé à ${r.email}`);
    } catch (e) {
      setToast(`❌ Erreur : ${e.message}`);
    } finally {
      removeLoading(userId);
    }
  }

  async function verifyUser(userId, currentlyVerified) {
    addLoading(userId);
    const endpoint = currentlyVerified ? "unverify" : "verify";
    try {
      const r = await api(`/api/admin/${endpoint}/${userId}`, { method: "POST" });
      if (currentlyVerified) {
        setUnverified(prev => new Set([...prev, userId]));
        setVerified(prev => { const n = new Set(prev); n.delete(userId); return n; });
        setToast("Certification retirée");
      } else {
        setVerified(prev => new Set([...prev, userId]));
        setUnverified(prev => { const n = new Set(prev); n.delete(userId); return n; });
        setToast(`✓ Profil certifié — email envoyé à ${r.email}`);
      }
    } catch (e) {
      setToast(`❌ Erreur : ${e.message}`);
    } finally {
      removeLoading(userId);
    }
  }

  async function sendTest() {
    setTestLoading(true);
    setTestResult(null);
    try {
      const r = await api("/api/admin/test-email", {
        method: "POST",
        body: JSON.stringify({ type: testType }),
      });
      setTestResult({ ok: true, msg: `✉ Email de test envoyé à ${r.to}` });
    } catch (e) {
      setTestResult({ ok: false, msg: `❌ Erreur : ${e.message}` });
    } finally {
      setTestLoading(false);
    }
  }

  async function rejectVerification(userId) {
    addLoading(userId);
    try {
      const r = await api(`/api/admin/reject-verification/${userId}`, { method: "POST" });
      setRejected(prev => new Set([...prev, userId]));
      setToast(`✗ Demande refusée — email envoyé à ${r.email}`);
    } catch (e) {
      setToast(`❌ Erreur : ${e.message}`);
    } finally {
      removeLoading(userId);
    }
  }

  function isVerified(u) {
    if (verified.has(u.id))   return true;
    if (unverified.has(u.id)) return false;
    return !!u.is_verified;
  }

  if (loading) return (
    <div className="flex min-h-[50vh] items-center justify-center">
      <div className="h-8 w-8 animate-spin rounded-full border-2 border-sensoria-accent border-t-transparent" />
    </div>
  );

  if (error) return (
    <div className="flex min-h-[50vh] flex-col items-center justify-center gap-3 text-center">
      <span className="text-5xl">🔒</span>
      <p className="font-semibold text-sensoria-danger">Accès non autorisé.</p>
      <p className="text-sm text-sensoria-muted">Vérifie ADMIN_EMAILS dans le .env</p>
    </div>
  );

  const { users, profiles, content, subscriptions, recent_users, users_without_profile, users_incomplete, daily_signups, pending_verifications } = stats;
  const totalPremium = (subscriptions || []).reduce((s, p) => s + p.count, 0);

  const filteredUsers = (recent_users || []).filter(u =>
    !search ||
    u.email?.toLowerCase().includes(search.toLowerCase()) ||
    u.username?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="mx-auto max-w-5xl space-y-6 px-1 pb-12 sm:px-2">
      <Toast msg={toast} onDone={() => setToast("")} />

      {/* ── Header ── */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-3">
          <a
            href="/"
            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl border border-sensoria-border/40 bg-sensoria-card text-white/50 transition-all hover:border-sensoria-accent/30 hover:text-white"
            title="Retour"
          >
            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
            </svg>
          </a>
          <div>
            <h1 className="text-xl font-bold text-white sm:text-2xl">⚙ Administration</h1>
            <p className="text-xs text-sensoria-muted">Univers Libertin · {fmtDate(new Date())}</p>
          </div>
        </div>
        {/* Tabs */}
        <div className="flex flex-wrap gap-1 rounded-xl border border-sensoria-border/40 bg-sensoria-card p-1">
          {TABS.map(t => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              className={`flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-medium transition-all ${
                tab === t.key
                  ? "bg-sensoria-accent text-white shadow-sm"
                  : "text-sensoria-muted hover:text-white"
              }`}
            >
              <span>{t.icon}</span>
              <span className="hidden sm:inline">{t.label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* ══════════════════════════════════
          TAB — Vue d'ensemble
         ══════════════════════════════════ */}
      {tab === "overview" && (
        <div className="space-y-6">

          <section className="space-y-3">
            <SectionTitle>👥 Inscriptions</SectionTitle>
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
              <KPI icon="👥" label="Total inscrits" value={users.total} accent />
              <KPI icon="🆕" label="Aujourd'hui"    value={users.today} />
              <KPI icon="📅" label="Cette semaine"  value={users.this_week} />
              <KPI icon="📆" label="Ce mois"         value={users.this_month} />
            </div>
          </section>

          <section className="space-y-3">
            <SectionTitle>👤 Profils &amp; Activation</SectionTitle>
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
              <KPI icon="📋" label="Profils créés"   value={profiles.total} sub={`${profiles.activation_rate}% des inscrits`} />
              <KPI icon="✅" label="Profils complets" value={profiles.complete} sub="bio + ville + âge + genre" />
              <KPI icon="👁" label="Profils visibles" value={profiles.visible} sub="dans l'annuaire" accent />
              <KPI icon="⚠️" label="Sans profil"      value={users.no_profile} sub="jamais rempli" alert={users.no_profile > 0} />
            </div>

            <div className="rounded-2xl border border-sensoria-border/30 bg-sensoria-card p-4">
              <div className="mb-2 flex items-center justify-between text-xs">
                <span className="font-medium text-sensoria-text-dim">Taux d'activation</span>
                <span className="font-bold text-white">{profiles.activation_rate}%</span>
              </div>
              <div className="h-2 overflow-hidden rounded-full bg-white/5">
                <div
                  className="h-full rounded-full bg-gradient-to-r from-sensoria-accent to-purple-400 transition-all duration-1000"
                  style={{ width: `${profiles.activation_rate}%` }}
                />
              </div>
              <p className="mt-2 text-[10px] text-sensoria-muted">
                {profiles.total} profils créés sur {users.total} inscrits · {profiles.visible} visibles · <strong className="text-sensoria-accent-light">{profiles.verified || 0} certifiés ✓</strong>
              </p>
            </div>
          </section>

          <section className="space-y-3">
            <SectionTitle>📊 Contenu</SectionTitle>
            <div className="grid grid-cols-3 gap-3 sm:grid-cols-6">
              <KPI icon="💬" label="Posts"      value={content.posts} />
              <KPI icon="🗨" label="Comments"   value={content.comments} />
              <KPI icon="❤️" label="Likes"      value={content.likes} />
              <KPI icon="✉️" label="Messages"   value={content.messages} />
              <KPI icon="🔥" label="Rencontres" value={content.encounters} />
              <KPI icon="📍" label="Lieux"      value={content.places} />
            </div>
          </section>

          {(subscriptions?.length > 0 || totalPremium === 0) && (
            <section className="space-y-3">
              <SectionTitle>💜 Abonnements actifs</SectionTitle>
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                <KPI icon="💜" label="Total payants" value={totalPremium} accent={totalPremium > 0} />
                {(subscriptions || []).map(s => (
                  <KPI
                    key={s.plan}
                    icon={s.plan === "vip" ? "⭐" : s.plan === "pro" ? "🏢" : "💎"}
                    label={s.plan.toUpperCase()}
                    value={s.count}
                  />
                ))}
              </div>
            </section>
          )}

          {daily_signups?.length > 0 && (
            <section className="space-y-3">
              <SectionTitle>📈 Inscriptions J-30</SectionTitle>
              <div className="rounded-2xl border border-sensoria-border/30 bg-sensoria-card p-4">
                <div className="mb-4 flex items-end justify-between gap-4">
                  <div>
                    <p className="text-2xl font-bold text-white">{users.this_month}</p>
                    <p className="text-xs text-sensoria-muted">inscrits ce mois</p>
                  </div>
                  <Sparkline data={daily_signups} width={200} height={50} />
                </div>
                <div className="mt-3 space-y-1 max-h-48 overflow-y-auto">
                  {[...daily_signups].reverse().slice(0, 10).map(d => {
                    const max = Math.max(...daily_signups.map(x => x.count), 1);
                    const pct = Math.round((d.count / max) * 100);
                    return (
                      <div key={d.day} className="flex items-center gap-3">
                        <span className="w-16 shrink-0 text-right text-[11px] text-sensoria-muted">{fmtDate(d.day)}</span>
                        <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-white/5">
                          <div className="h-full rounded-full bg-sensoria-accent/60" style={{ width: `${pct}%` }} />
                        </div>
                        <span className="w-4 text-right text-[11px] font-semibold text-white">{d.count}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            </section>
          )}
        </div>
      )}

      {/* ══════════════════════════════════
          TAB — Inscrits
         ══════════════════════════════════ */}
      {tab === "users" && (
        <div className="space-y-4">
          <div className="flex items-center gap-3">
            <div className="relative flex-1">
              <svg className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-sensoria-muted" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              <input
                type="text"
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="Rechercher…"
                className="sensoria-input w-full pl-9 text-sm"
              />
            </div>
            <span className="shrink-0 text-xs text-sensoria-muted">{filteredUsers.length} / {recent_users?.length}</span>
          </div>

          <div className="overflow-hidden rounded-2xl border border-sensoria-border/30 bg-sensoria-card">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-sensoria-border/40 bg-sensoria-surface/30">
                    <th className="px-4 py-3 text-left text-[10px] font-bold uppercase tracking-wider text-sensoria-muted">Pseudo</th>
                    <th className="px-4 py-3 text-left text-[10px] font-bold uppercase tracking-wider text-sensoria-muted hidden sm:table-cell">Email</th>
                    <th className="px-4 py-3 text-left text-[10px] font-bold uppercase tracking-wider text-sensoria-muted hidden md:table-cell">Ville</th>
                    <th className="px-4 py-3 text-left text-[10px] font-bold uppercase tracking-wider text-sensoria-muted">Plan</th>
                    <th className="px-4 py-3 text-left text-[10px] font-bold uppercase tracking-wider text-sensoria-muted hidden sm:table-cell">Profil</th>
                    <th className="px-4 py-3 text-right text-[10px] font-bold uppercase tracking-wider text-sensoria-muted">Inscrit</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-sensoria-border/20">
                  {filteredUsers.map(u => (
                    <tr key={u.id} className="group transition-colors hover:bg-sensoria-surface/30">
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-sensoria-accent/10 text-xs font-semibold text-sensoria-accent">
                            {(u.username || "?")[0].toUpperCase()}
                          </div>
                          <span className="font-medium text-white">{u.username}</span>
                          {isVerified(u) && <VerifiedBadge />}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-xs text-sensoria-text-dim hidden sm:table-cell">{u.email}</td>
                      <td className="px-4 py-3 text-xs text-sensoria-muted hidden md:table-cell">
                        {u.city ? `${u.city}${u.department ? ` (${u.department})` : ""}` : "—"}
                      </td>
                      <td className="px-4 py-3"><PlanBadge plan={u.plan} /></td>
                      <td className="px-4 py-3 hidden sm:table-cell">
                        {u.city && u.gender && u.age
                          ? <span className="text-[10px] text-green-400">✓ Complet</span>
                          : u.city || u.gender
                            ? <span className="text-[10px] text-amber-400">⚠ Partiel</span>
                            : <span className="text-[10px] text-sensoria-muted">—</span>
                        }
                      </td>
                      <td className="px-4 py-3 text-right text-xs text-sensoria-muted whitespace-nowrap">{timeAgo(u.created_at)}</td>
                    </tr>
                  ))}
                  {filteredUsers.length === 0 && (
                    <tr>
                      <td colSpan={6} className="py-10 text-center text-sm text-sensoria-muted">Aucun résultat</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* ══════════════════════════════════
          TAB — Activation
         ══════════════════════════════════ */}
      {tab === "activation" && (
        <div className="space-y-4">
          <div className="rounded-2xl border border-orange-500/20 bg-orange-500/5 p-4">
            <div className="flex items-start gap-3">
              <span className="text-2xl">⚠️</span>
              <div>
                <p className="font-semibold text-orange-300">{users.no_profile} inscrits sans profil</p>
                <p className="mt-0.5 text-xs text-sensoria-muted">
                  Ces membres ne sont pas dans l'annuaire. Envoie-leur un email de relance.
                </p>
              </div>
            </div>
          </div>

          <div className="rounded-2xl border border-sensoria-border/30 bg-sensoria-card p-4 space-y-2">
            <h3 className="text-sm font-semibold text-white">📋 Pourquoi l'annuaire est vide ?</h3>
            <p className="text-xs leading-relaxed text-sensoria-text-dim">
              Un profil dans l'annuaire nécessite que l'inscrit ait rempli son profil (<code className="text-sensoria-accent">/profile</code>)
              ET que <code className="text-sensoria-accent">is_visible = 1</code>. Les nouveaux inscrits n'ont pas de profil par défaut.
            </p>
            <div className="grid grid-cols-3 gap-2 pt-1">
              <div className="rounded-xl border border-sensoria-border/20 bg-sensoria-surface/30 p-3 text-center">
                <p className="text-lg font-bold text-white">{users.total}</p>
                <p className="text-[10px] text-sensoria-muted">Inscrits</p>
              </div>
              <div className="rounded-xl border border-sensoria-border/20 bg-sensoria-surface/30 p-3 text-center">
                <p className="text-lg font-bold text-sensoria-accent-light">{profiles.total}</p>
                <p className="text-[10px] text-sensoria-muted">Profils créés</p>
              </div>
              <div className="rounded-xl border border-green-500/20 bg-green-500/5 p-3 text-center">
                <p className="text-lg font-bold text-green-400">{profiles.visible}</p>
                <p className="text-[10px] text-sensoria-muted">Dans l'annuaire</p>
              </div>
            </div>
          </div>

          {/* Sans profil */}
          <div>
            <h3 className="mb-2 text-xs font-bold uppercase tracking-wider text-sensoria-muted">Sans profil (20 derniers)</h3>
            <div className="overflow-hidden rounded-2xl border border-sensoria-border/30 bg-sensoria-card">
              {(users_without_profile || []).length === 0 ? (
                <p className="py-8 text-center text-sm text-green-400">✓ Tous les inscrits ont un profil</p>
              ) : (
                <div className="divide-y divide-sensoria-border/20">
                  {(users_without_profile || []).map(u => (
                    <div key={u.id} className="flex items-center gap-3 px-4 py-3">
                      <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-orange-500/10 text-sm font-semibold text-orange-400">
                        {(u.username || "?")[0].toUpperCase()}
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium text-white truncate">{u.username}</p>
                        <p className="text-[11px] text-sensoria-muted truncate">{u.email} · inscrit {timeAgo(u.created_at)}</p>
                      </div>
                      <button
                        onClick={() => sendReminder(u.id)}
                        disabled={reminded.has(u.id) || loadingIds.has(u.id)}
                        className={`shrink-0 rounded-lg px-3 py-1.5 text-xs font-medium transition-all ${
                          reminded.has(u.id)
                            ? "bg-green-500/10 text-green-400 cursor-default"
                            : loadingIds.has(u.id)
                              ? "bg-sensoria-accent/10 text-sensoria-muted cursor-wait"
                              : "bg-sensoria-accent/15 text-sensoria-accent-light hover:bg-sensoria-accent/25"
                        }`}
                      >
                        {loadingIds.has(u.id) ? "…" : reminded.has(u.id) ? "✓ Envoyé" : "✉ Relancer"}
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Profils incomplets */}
          {(users_incomplete || []).length > 0 && (
            <div>
              <h3 className="mb-2 text-xs font-bold uppercase tracking-wider text-sensoria-muted">Profils incomplets (30 derniers)</h3>
              <div className="overflow-hidden rounded-2xl border border-sensoria-border/30 bg-sensoria-card">
                <div className="divide-y divide-sensoria-border/20">
                  {(users_incomplete || []).map(u => {
                    const missing = [];
                    if (!u.bio || u.bio === "")     missing.push("bio");
                    if (!u.city || u.city === "")   missing.push("ville");
                    if (!u.age)                     missing.push("âge");
                    if (!u.gender || u.gender === "") missing.push("genre");
                    return (
                      <div key={u.id} className="flex items-center gap-3 px-4 py-3">
                        <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-amber-500/10 text-sm font-semibold text-amber-400">
                          {(u.username || "?")[0].toUpperCase()}
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-medium text-white truncate">{u.username}</p>
                          <p className="text-[11px] text-amber-500/70 truncate">Manque : {missing.join(", ")}</p>
                        </div>
                        <button
                          onClick={() => sendReminder(u.id)}
                          disabled={reminded.has(u.id) || loadingIds.has(u.id)}
                          className={`shrink-0 rounded-lg px-3 py-1.5 text-xs font-medium transition-all ${
                            reminded.has(u.id)
                              ? "bg-green-500/10 text-green-400 cursor-default"
                              : loadingIds.has(u.id)
                                ? "bg-amber-500/10 text-sensoria-muted cursor-wait"
                                : "bg-amber-500/15 text-amber-300 hover:bg-amber-500/25"
                          }`}
                        >
                          {loadingIds.has(u.id) ? "…" : reminded.has(u.id) ? "✓ Envoyé" : "✉ Relancer"}
                        </button>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ══════════════════════════════════
          TAB — Vérification
         ══════════════════════════════════ */}
      {tab === "verification" && (
        <div className="space-y-5">

          {/* Explication */}
          <div className="rounded-2xl border border-sensoria-accent/20 bg-sensoria-accent/5 p-4">
            <div className="flex items-start gap-3">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-sensoria-accent text-white font-bold text-sm">✓</div>
              <div>
                <p className="font-semibold text-white">Badge de certification de profil</p>
                <p className="mt-1 text-xs leading-relaxed text-sensoria-text-dim">
                  Le badge <strong className="text-sensoria-accent-light">✓ Certifié</strong> s'affiche sur le profil et dans l'annuaire.
                  Il indique que l'identité a été vérifiée par l'équipe. La certification envoie automatiquement un email à l'utilisateur.
                </p>
              </div>
            </div>
          </div>

          {/* ── Demandes de certification en attente ── */}
          {(() => {
            const pendingList = (pending_verifications || []).filter(vr => !rejected.has(vr.user_id) && !verified.has(vr.user_id));
            const count = pendingList.length;
            return (
              <div className={`rounded-2xl border p-4 space-y-3 ${
                count > 0
                  ? "border-amber-500/30 bg-amber-500/5"
                  : "border-sensoria-border/30 bg-sensoria-card"
              }`}>
                <div className="flex items-center justify-between">
                  <h3 className="text-sm font-semibold text-white flex items-center gap-2">
                    <span>📥</span>
                    Demandes de certification
                    {count > 0 && (
                      <span className="rounded-full bg-amber-500 px-2 py-0.5 text-[10px] font-bold text-white">{count}</span>
                    )}
                  </h3>
                </div>
                {count === 0 ? (
                  <p className="text-sm text-sensoria-muted">Aucune demande en attente.</p>
                ) : (
                  <div className="space-y-3">
                    {pendingList.map(vr => (
                      <div key={vr.user_id} className="rounded-xl border border-amber-500/20 bg-sensoria-card p-4">
                        <div className="flex items-start gap-3">
                          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-amber-500/15 text-sm font-bold text-amber-400">
                            {(vr.username || "?")[0].toUpperCase()}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex flex-wrap items-center gap-2">
                              <span className="font-semibold text-white">{vr.username}</span>
                              <span className="text-[10px] text-sensoria-muted">{vr.email}</span>
                              {vr.city && <span className="text-[10px] text-sensoria-muted">· {vr.city}</span>}
                              {vr.age && <span className="text-[10px] text-sensoria-muted">· {vr.age} ans</span>}
                              {vr.gender && <span className="text-[10px] text-sensoria-muted">· {vr.gender}</span>}
                            </div>
                            {vr.bio && (
                              <p className="mt-1 text-xs text-sensoria-text-dim line-clamp-2">{vr.bio}</p>
                            )}
                            {/* Photo de vérification */}
                            {vr.photo_verification_url && (
                              <div className="mt-2">
                                <p className="mb-1.5 text-[10px] font-bold uppercase tracking-wider text-amber-400">Photo de vérification :</p>
                                <a
                                  href={vr.photo_verification_url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="group relative block w-fit"
                                >
                                  <img
                                    src={vr.photo_verification_url}
                                    alt="Photo de vérification"
                                    className="max-h-48 max-w-xs rounded-lg border border-amber-500/30 object-contain transition-all group-hover:border-amber-400/60"
                                  />
                                  <span className="absolute inset-0 flex items-center justify-center rounded-lg bg-black/40 opacity-0 transition-opacity group-hover:opacity-100 text-xs text-white font-medium">
                                    🔍 Agrandir
                                  </span>
                                </a>
                              </div>
                            )}
                            {!vr.photo_verification_url && (
                              <div className="mt-2 rounded-lg border border-red-500/20 bg-red-500/5 px-3 py-2">
                                <p className="text-[10px] text-red-400">⚠ Aucune photo de vérification fournie</p>
                              </div>
                            )}
                            {vr.message && (
                              <div className="mt-2 rounded-lg border border-amber-500/20 bg-amber-500/5 px-3 py-2">
                                <p className="text-[10px] font-bold uppercase tracking-wider text-amber-400 mb-1">Message :</p>
                                <p className="text-xs text-sensoria-text-dim italic">"{vr.message}"</p>
                              </div>
                            )}
                            <p className="mt-2 text-[10px] text-sensoria-muted">Demande le {fmtDate(vr.created_at)}</p>
                          </div>
                        </div>
                        <div className="mt-3 flex items-center gap-2 justify-end">
                          <button
                            onClick={() => rejectVerification(vr.user_id)}
                            disabled={loadingIds.has(vr.user_id)}
                            className="rounded-lg border border-red-500/30 bg-red-500/10 px-3 py-1.5 text-xs font-semibold text-red-400 hover:bg-red-500/20 transition-all disabled:opacity-50"
                          >
                            {loadingIds.has(vr.user_id) ? "…" : "✗ Refuser"}
                          </button>
                          <button
                            onClick={() => verifyUser(vr.user_id, false)}
                            disabled={loadingIds.has(vr.user_id)}
                            className="rounded-lg bg-sensoria-accent px-4 py-1.5 text-xs font-semibold text-white hover:bg-sensoria-accent/80 transition-all disabled:opacity-50"
                          >
                            {loadingIds.has(vr.user_id) ? "…" : "✓ Approuver"}
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })()}

          {/* Stats KPI en ligne sous les demandes */}
          <div className="grid grid-cols-3 gap-3">
            <KPI icon="✓"  label="Certifiés"             value={profiles.verified || 0} accent />
            <KPI icon="⏳" label="Demandes en attente"   value={profiles.pending_verifications || 0} alert={(profiles.pending_verifications || 0) > 0} />
            <KPI icon="👤" label="Profils total"          value={profiles.total} />
          </div>

          {/* Test email */}
          <div className="rounded-2xl border border-sensoria-border/30 bg-sensoria-card p-5 space-y-4">
            <h3 className="text-sm font-semibold text-white flex items-center gap-2">
              <span className="text-lg">🧪</span> Tester les emails
            </h3>
            <p className="text-xs text-sensoria-text-dim">
              Envoie un email de test à ton adresse admin pour visualiser le rendu avant de l'envoyer aux membres.
            </p>
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
              <div className="flex gap-2">
                <button
                  onClick={() => setTestType("reminder")}
                  className={`rounded-lg px-3 py-2 text-xs font-medium transition-all border ${
                    testType === "reminder"
                      ? "border-sensoria-accent bg-sensoria-accent/15 text-sensoria-accent-light"
                      : "border-sensoria-border/30 text-sensoria-muted hover:text-white"
                  }`}
                >
                  ✉ Relance profil
                </button>
                <button
                  onClick={() => setTestType("verified")}
                  className={`rounded-lg px-3 py-2 text-xs font-medium transition-all border ${
                    testType === "verified"
                      ? "border-sensoria-accent bg-sensoria-accent/15 text-sensoria-accent-light"
                      : "border-sensoria-border/30 text-sensoria-muted hover:text-white"
                  }`}
                >
                  ✓ Certification
                </button>
              </div>
              <button
                onClick={sendTest}
                disabled={testLoading}
                className="ml-auto rounded-lg bg-sensoria-accent px-4 py-2 text-xs font-semibold text-white transition-all hover:bg-sensoria-accent/80 disabled:opacity-50"
              >
                {testLoading ? "Envoi…" : "Envoyer le test →"}
              </button>
            </div>
            {testResult && (
              <div className={`rounded-lg px-3 py-2 text-xs font-medium ${
                testResult.ok
                  ? "bg-green-500/10 text-green-400 border border-green-500/20"
                  : "bg-red-500/10 text-red-400 border border-red-500/20"
              }`}>
                {testResult.msg}
              </div>
            )}
          </div>

          {/* Liste des profils avec actions certifier/décertifier */}
          <div>
            <h3 className="mb-2 text-xs font-bold uppercase tracking-wider text-sensoria-muted">Profils membres — gérer la certification</h3>
            <div className="overflow-hidden rounded-2xl border border-sensoria-border/30 bg-sensoria-card">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-sensoria-border/40 bg-sensoria-surface/30">
                      <th className="px-4 py-3 text-left text-[10px] font-bold uppercase tracking-wider text-sensoria-muted">Membre</th>
                      <th className="px-4 py-3 text-left text-[10px] font-bold uppercase tracking-wider text-sensoria-muted hidden sm:table-cell">Email</th>
                      <th className="px-4 py-3 text-left text-[10px] font-bold uppercase tracking-wider text-sensoria-muted">Statut</th>
                      <th className="px-4 py-3 text-right text-[10px] font-bold uppercase tracking-wider text-sensoria-muted">Action</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-sensoria-border/20">
                    {(recent_users || []).filter(u => u.city || u.gender || u.age).map(u => {
                      const certifié = isVerified(u);
                      return (
                        <tr key={u.id} className="group hover:bg-sensoria-surface/30">
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-2">
                              <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-sensoria-accent/10 text-xs font-semibold text-sensoria-accent">
                                {(u.username || "?")[0].toUpperCase()}
                              </div>
                              <span className="font-medium text-white">{u.username}</span>
                              {certifié && <VerifiedBadge />}
                            </div>
                          </td>
                          <td className="px-4 py-3 text-xs text-sensoria-text-dim hidden sm:table-cell">{u.email}</td>
                          <td className="px-4 py-3">
                            {certifié
                              ? <span className="inline-flex items-center gap-1 rounded-full bg-sensoria-accent/15 px-2 py-0.5 text-[10px] font-semibold text-sensoria-accent-light">✓ Certifié</span>
                              : <span className="text-[10px] text-sensoria-muted">Non certifié</span>
                            }
                          </td>
                          <td className="px-4 py-3 text-right">
                            <button
                              onClick={() => verifyUser(u.id, certifié)}
                              disabled={loadingIds.has(u.id)}
                              className={`rounded-lg px-3 py-1.5 text-xs font-medium transition-all ${
                                loadingIds.has(u.id)
                                  ? "opacity-50 cursor-wait"
                                  : certifié
                                    ? "bg-red-500/10 text-red-400 hover:bg-red-500/20"
                                    : "bg-sensoria-accent/15 text-sensoria-accent-light hover:bg-sensoria-accent/25"
                              }`}
                            >
                              {loadingIds.has(u.id) ? "…" : certifié ? "Retirer ✓" : "Certifier ✓"}
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
